import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/shadcn/card';
import { get } from '../utils/api';

// Shown until the API has a cached copy of our Google reviews (env not configured
// yet, or the endpoint is unreachable) — the same four testimonials the page has
// always carried, so the section never renders empty.
const FALLBACK_REVIEWS = [
  {
    authorName: 'Levi M',
    text: 'My daughter loves learning from Addy, it really keeps learning fun and engaging!',
  },
  {
    authorName: 'Jackie P',
    text: "My daughter's confidence in maths has grown tremendously since we started using Nova, I've never seen her this excited about Maths!",
  },
  {
    authorName: 'Kehinde O',
    text: 'Learning with Nova has been a game changer for my son, he always looks forward to his classroom sessions!',
  },
  {
    authorName: 'Sandra J',
    text: "I've never seen my son so engaged while learning! He absolutely loves doing his lessons with Nova.",
  },
];

// Alternating card colours, matching the old testimonial grid.
const CARD_STYLES = [
  'bg-[#EBF7D4] border-[#C5E39C]',
  'bg-[#DDD6F5] border-[#C4B7EA]',
];

export const GoogleG = ({ className }) => (
  <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z" />
    <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z" />
    <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34A21.94 21.94 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z" />
    <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z" />
  </svg>
);

export const Stars = ({ rating }) => (
  <span className="text-base leading-none" aria-label={`${rating} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <span key={i} className={i <= Math.round(rating) ? 'text-[#FBBC04]' : 'text-gray-300'}>
        ★
      </span>
    ))}
  </span>
);

const ReviewCard = ({ review, styleIdx, fromGoogle }) => (
  <Card className={`w-[320px] sm:w-[360px] shrink-0 border-2 ${CARD_STYLES[styleIdx % 2]}`}>
    <CardContent className="p-6 h-full">
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-3 mb-1">
          {fromGoogle && review.authorPhotoUrl ? (
            <img
              src={review.authorPhotoUrl}
              alt=""
              referrerPolicy="no-referrer"
              className="w-9 h-9 rounded-full"
            />
          ) : null}
          <div>
            <h3 className="text-lg font-semibold text-black" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {review.authorName}
            </h3>
            {fromGoogle ? (
              <Stars rating={review.rating} />
            ) : (
              <p className="text-sm text-gray-600" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Parent
              </p>
            )}
          </div>
        </div>
        <p
          className="text-black leading-relaxed mt-3 line-clamp-5"
          style={{ fontFamily: 'Fredoka, sans-serif' }}
        >
          {review.text}
        </p>
        {fromGoogle && (
          <div className="flex items-center gap-2 mt-auto pt-4 text-sm text-gray-600"
               style={{ fontFamily: 'Poppins, sans-serif' }}>
            <GoogleG className="w-4 h-4" />
            <span>{review.relativeTime ? `${review.relativeTime} · ` : ''}posted on Google</span>
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

/**
 * Our Google Business reviews from the API's daily cache (see api/src/google-reviews).
 * `null` until the request resolves; stays `null` if the endpoint is unreachable
 * (e.g. local dev without the API) so callers fall back to built-in content.
 * Fetched once by the landing page and shared with the stats strip and the reel.
 */
export function useGoogleReviews() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    get('/google-reviews')
      .then((payload) => {
        if (!cancelled) setData(payload);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return data;
}

/**
 * Auto-scrolling reel of our Google Business reviews. The track is rendered twice
 * and translated by -50%, so the loop wraps seamlessly; hovering pauses it so
 * reviews are readable. `data` is the useGoogleReviews() payload.
 */
function GoogleReviewsReel({ data }) {
  const fromGoogle = !!data?.configured && data.reviews.length > 0;
  const reviews = fromGoogle ? data.reviews : FALLBACK_REVIEWS;
  // ~8s of travel per card keeps the speed steady however many reviews Google returns.
  const duration = Math.max(reviews.length * 8, 24);

  return (
    <div className="mt-12">
      <style>{`
        @keyframes reviews-marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        /* !important: the animation is set via an inline shorthand (duration depends on
           review count), and inline styles would otherwise win over this hover rule. */
        .reviews-reel:hover .reviews-track,
        .reviews-reel:focus-within .reviews-track {
          animation-play-state: paused !important;
        }
      `}</style>

      {fromGoogle && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6">
          <div className="flex items-center gap-2 text-black" style={{ fontFamily: 'Poppins, sans-serif' }}>
            <GoogleG className="w-5 h-5" />
            <span className="font-semibold">{data.rating?.toFixed(1)}</span>
            <Stars rating={data.rating || 0} />
            <span className="text-gray-600">from {data.totalReviews} Google reviews</span>
          </div>
          {data.googleMapsUrl && (
            <a
              href={data.googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#2F1E6B] font-semibold hover:underline"
              style={{ fontFamily: 'Fredoka, sans-serif' }}
            >
              Read all our reviews on Google →
            </a>
          )}
        </div>
      )}

      <div className="reviews-reel overflow-hidden" aria-label="Parent reviews carousel">
        {/* No gap on the track itself: each copy carries pr-6, so -50% lands exactly
            one copy-width along and the loop wraps without a visible jump. */}
        <div
          className="reviews-track flex w-max"
          style={{ animation: `reviews-marquee ${duration}s linear infinite` }}
        >
          {[0, 1].map((copy) => (
            <div key={copy} className="flex gap-6 pr-6" aria-hidden={copy === 1}>
              {reviews.map((review, idx) => (
                <ReviewCard
                  key={`${copy}-${idx}`}
                  review={review}
                  styleIdx={idx}
                  fromGoogle={fromGoogle}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default GoogleReviewsReel;
